import React from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import { defineMessages, injectIntl, WrappedComponentProps } from 'react-intl';
import { Eye, EyeOff } from '@lifeomic/chromicons';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { GlobalHotKeys } from 'react-hotkeys';
import { getTestProps } from '../../util/testProps';

export const testIds = {
  button: 'maskedModeSwitch-toggleId',
};

const KEY_MAP = {
  TOGGLE_MASKED_MODE: 'm',
};

const styles = (_theme: Theme) =>
  createStyles({
    iconContainer: {
      display: 'inline-block',
      '&:hover': {
        cursor: 'pointer',
      },
      '&:focus': {
        outline: 'none',
      },
    },
  });

const messages = defineMessages({
  toggleMaskedMode: {
    id: 'maskedModeSwitch.toggleMaskedMode',
    defaultMessage: 'Toggle Masked Mode',
  },
});

export interface MaskedModeSwitchOwnProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  setMaskedMode(isActive: boolean): void;
  isForced: boolean;
  isActive: boolean;
}

export type MaskedModeSwitchInjectedProps = WithStyles<typeof styles> &
  WrappedComponentProps;

export type MaskedModeSwitchProps = MaskedModeSwitchOwnProps &
  MaskedModeSwitchInjectedProps;

export class MaskedModeSwitchComponent extends React.PureComponent<MaskedModeSwitchProps> {
  private handleToggleMaskedMode = () => {
    const { setMaskedMode, isActive } = this.props;
    setMaskedMode(!isActive);
  };

  public render() {
    const {
      classes,
      isActive,
      isForced,
      intl: { formatMessage },
      setMaskedMode,
      ...rootProps
    } = this.props;

    const tooltip = isForced
      ? formatMessage({
          id: 'maskedMode.toggle.forcedTooltip',
          defaultMessage:
            'Subject identifying information is unavailable due to access controls',
        })
      : formatMessage({
          id: 'maskedMode.toggle.tooltip',
          defaultMessage:
            'Masked mode will mask identifying information on subjects',
        });

    const { EyeIcon, testId } =
      !isForced && isActive
        ? { EyeIcon: EyeOff, testId: 'eye-off-icon' }
        : { EyeIcon: Eye, testId: 'eye-on-icon' };

    const HANDLERS = {
      TOGGLE_MASKED_MODE: this.handleToggleMaskedMode,
    };

    return (
      <GlobalHotKeys keyMap={KEY_MAP} handlers={HANDLERS}>
        <div {...rootProps}>
          <Tooltip title={tooltip} placement="left">
            <div className={classes.iconContainer}>
              <IconButton
                data-testid={testId}
                id="masked-mode-toggle"
                aria-label={formatMessage(messages.toggleMaskedMode)}
                color="inverse"
                disabled={isForced}
                onClick={this.handleToggleMaskedMode}
                icon={EyeIcon}
                size={0}
                {...getTestProps(testIds.button)}
              />
            </div>
          </Tooltip>
        </div>
      </GlobalHotKeys>
    );
  }
}

export default injectIntl(withStyles(styles)(MaskedModeSwitchComponent));
